// src/utils/mockDataProjectGaps.ts
// ─────────────────────────────────────────────────────────────────
// Phase 1 Gap-Fill Utility
// Bridges the Estimating → Jobs transition (PM-01, PM-02)
// Stores drawing pins (PM-04), WI sign-offs (PM-06), agent activity (PM-14)
// ─────────────────────────────────────────────────────────────────

// ── Types ─────────────────────────────────────────────────────────

/** Tracks a tender that has been formally awarded and converted to a Job */
export interface AwardEvent {
  id: string
  tenderId: string
  tenderReference: string
  jobId: string             // the new job ID created
  jobNumber: string         // e.g. TP-2026-007
  awardedAmount: number     // ex GST
  contractNumber: string
  contractStartDate: string
  recordedAt: string
  recordedByName: string
  boqLineCount: number      // how many BOQ lines were imported
  milestoneCount: number
}

/** A field-crew digital sign-off on a WI step */
export interface WIStepSignOff {
  id: string
  wiId: string
  stepNumber: number
  stepTitle: string
  signedByName: string
  signedByTier: string
  signedAt: string
  note?: string
  gpsLatitude?: number
  gpsLongitude?: number
}

/** An interactive pin on a drawing (links WI, ITP hold point, issue, photo, etc.) */
export type DrawingPinType = 'WI' | 'ITP_HOLD' | 'ISSUE' | 'QA_PASS' | 'QA_FAIL' | 'PHOTO'

export interface DrawingPin {
  id: string
  drawingDocId: string
  jobId: string
  type: DrawingPinType
  entityId: string          // WI id, hold point id, etc.
  entityLabel: string       // short label shown on pin
  entityStatus?: string
  xPct: number              // 0–100 percentage across drawing
  yPct: number              // 0–100 percentage down drawing
  createdAt: string
  createdByName: string
}

/** Agent activity log entry visible on the Job record (PM-14) */
export interface AgentActivityEntry {
  id: string
  jobId: string
  agentCode: string
  agentName: string
  agentWave: 'Wave 1' | 'Wave 2' | 'Wave 3' | 'Wave 4' | 'Wave 5'
  action: string
  detail: string
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
  autonomyLevel: 1 | 2 | 3   // 1=Advisory 2=Assisted 3=Automated
  timestamp: string
  humanResponse: 'ACCEPTED' | 'MODIFIED' | 'REJECTED' | 'PENDING' | 'IGNORED'
  linkedModule?: string
  linkedRecordId?: string
}

// ── In-memory stores (module-level mutable) ───────────────────────

let _awardEvents: AwardEvent[] = []
let _createdJobs: any[] = []   // jobs created via Award-to-Activate
let _createdContracts: any[] = []  // contracts auto-created from Award-to-Activate
let _wiSignOffs: WIStepSignOff[] = []
let _drawingPins: DrawingPin[] = seedDrawingPins()
let _agentActivity: AgentActivityEntry[] = seedAgentActivity()

// ── Job type inference ─────────────────────────────────────────────

function inferJobType(boqSections: string[], clientType: string): string {
  if (boqSections.includes('HV_ELECTRICAL')) return 'ENERGY_GRID'
  if (boqSections.includes('ELECTRICAL') && boqSections.includes('TRAFFIC_CONTROL')) return 'SIGNALS'
  if (boqSections.includes('ELECTRICAL') && clientType?.includes('TMR')) return 'SIGNALS'
  if (boqSections.includes('CIVIL')) return 'CIVIL'
  if (boqSections.includes('COMMISSIONING')) return 'ITS'
  return 'SIGNALS'
}

function inferContractType(clientType: string): string {
  if (clientType === 'TMR' || clientType?.includes('Roads')) return 'AS4000'
  if (clientType === 'NEC4') return 'NEC4'
  return 'LUMP_SUM'
}

function nextJobNumber(): string {
  // Count all created jobs (MOCK_JOBS base is ~6, we add from here)
  const base = 6 + _awardEvents.length + 1
  return `TP-2026-0${base.toString().padStart(2, '0')}`
}

// ── Award to Activate ──────────────────────────────────────────────

export interface AwardFormData {
  awardedAmount: number
  contractNumber: string
  contractStartDate: string
  targetEndDate: string
  siteAddress: string
  recordedByName: string
}

export function createJobFromTender(
  tender: {
    id: string
    reference: string
    projectName: string
    clientName: string
    clientType: string
    boqItems: Array<{ sectionCode: string; description: string; quantity: number; unit: string; unitRateCharged: number; totalCharged: number }>
    sections: Array<{ code: string; content: string }>
  },
  form: AwardFormData
): { job: any; event: AwardEvent } {
  const jobNumber = nextJobNumber()
  const jobId = `j_award_${Date.now()}`

  const boqSections = [...new Set(tender.boqItems.map(b => b.sectionCode))]

  // Map BOQ items → job bomLines
  const bomLines = tender.boqItems
    .filter(b => b.totalCharged > 0)
    .map((b, i) => ({
      id: `bom_award_${i}`,
      code: `BOQ-${i + 1}`,
      description: b.description,
      materialType: b.sectionCode === 'ELECTRICAL' || b.sectionCode === 'HV_ELECTRICAL' ? 'BUILD_MATERIAL' : 'BUILD_MATERIAL',
      qty: b.quantity,
      unit: b.unit,
      unitCost: b.unitRateCharged,
      total: b.totalCharged,
      status: 'NOT_ORDERED',
      poNumber: null,
    }))

  const scopeSection = tender.sections?.find(s => s.code === 'C')
  const descriptionLong = scopeSection?.content?.slice(0, 400) ?? tender.projectName

  const job = {
    id: jobId,
    jobNumber,
    description: tender.projectName,
    descriptionLong,
    type: inferJobType(boqSections, tender.clientType),
    clientId: 'c_award',
    clientName: tender.clientName,
    clientContact: '',
    pmId: 'pm_1',
    pmName: 'Jane PM',
    startDate: form.contractStartDate,
    targetEnd: form.targetEndDate,
    contractValue: form.awardedAmount,
    contractType: inferContractType(tender.clientType),
    contractNumber: form.contractNumber,
    siteAddress: form.siteAddress,
    status: 'PRE_START',
    progressPercent: 0,
    crew: 0,
    plantOnSite: 0,
    openIssues: 0,
    openHoldPoints: 0,
    openNCRs: 0,
    poCommitted: 0,
    bomLines,
    // Gap PM-01: tracks the originating tender
    _fromTenderId: tender.id,
    _fromTenderReference: tender.reference,
  }

  const event: AwardEvent = {
    id: `award_${Date.now()}`,
    tenderId: tender.id,
    tenderReference: tender.reference,
    jobId,
    jobNumber,
    awardedAmount: form.awardedAmount,
    contractNumber: form.contractNumber,
    contractStartDate: form.contractStartDate,
    recordedAt: new Date().toISOString(),
    recordedByName: form.recordedByName,
    boqLineCount: bomLines.length,
    milestoneCount: 0,
  }

  _awardEvents.push(event)
  _createdJobs.push(job)

  // Auto-create a contract record so Contracts module reflects the award immediately.
  // Retention: 5% held. LD rate: 0 pending PM to populate. Bond: none by default.
  const contract = {
    id:                      `con_award_${Date.now()}`,
    jobId,
    jobNumber,
    contractNumber:          form.contractNumber || `MCB-${jobNumber}`,
    clientName:              tender.clientName,
    superintendentName:      '',
    superintendentPhone:     '',
    contractType:            inferContractType(tender.clientType),
    originalContractValue:   form.awardedAmount,
    approvedVariationsValue: 0,
    retentionPercent:        5,
    retentionHeld:           Math.round(form.awardedAmount * 0.05),
    awardDate:               form.contractStartDate,
    commencementDate:        form.contractStartDate,
    practicalCompletionDate: form.targetEndDate || '',
    practicalCompletionActual: null,
    defectsLiabilityMonths:  12,
    liquidatedDamagesRate:   0,
    liquidatedDamagesActive: false,
    bondRequired:            false,
    bondValue:               null,
    bondExpiry:              null,
    notes:                   `Auto-created from tender ${tender.reference} — populate LD rate, bond and superintendent before site commencement.`,
    _fromTenderId:           tender.id,
    _fromTenderReference:    tender.reference,
  }
  _createdContracts.push(contract)

  return { job, event }
}

export function getAwardEvents(): AwardEvent[] { return [..._awardEvents] }
export function getCreatedJobs(): any[] { return [..._createdJobs] }
export function getCreatedContracts(): any[] { return [..._createdContracts] }

export function getAwardEventForJob(jobId: string): AwardEvent | undefined {
  return _awardEvents.find(e => e.jobId === jobId)
}

export function getAwardEventForTender(tenderId: string): AwardEvent | undefined {
  return _awardEvents.find(e => e.tenderId === tenderId)
}

// ── WI Step Sign-Offs ──────────────────────────────────────────────

export function recordWIStepSignOff(signOff: Omit<WIStepSignOff, 'id'>): WIStepSignOff {
  const entry: WIStepSignOff = { id: `signoff_${Date.now()}`, ...signOff }
  _wiSignOffs.push(entry)
  return entry
}

export function getWISignOffs(wiId: string): WIStepSignOff[] {
  return _wiSignOffs.filter(s => s.wiId === wiId)
}

export function getStepSignOff(wiId: string, stepNumber: number): WIStepSignOff | undefined {
  return _wiSignOffs.find(s => s.wiId === wiId && s.stepNumber === stepNumber)
}

export function isAllStepsSigned(wiId: string, totalSteps: number): boolean {
  const signed = new Set(_wiSignOffs.filter(s => s.wiId === wiId).map(s => s.stepNumber))
  return signed.size >= totalSteps
}

// ── Drawing Pins ───────────────────────────────────────────────────

function seedDrawingPins(): DrawingPin[] {
  return [
    { id: 'dp1', drawingDocId: 'doc_1', jobId: 'j1', type: 'WI', entityId: 'wi_jp1', entityLabel: 'WI-J001', entityStatus: 'DEPLOYED', xPct: 32, yPct: 45, createdAt: '2026-06-10T08:00:00Z', createdByName: 'Jane PM' },
    { id: 'dp2', drawingDocId: 'doc_1', jobId: 'j1', type: 'ITP_HOLD', entityId: 'hp_1', entityLabel: 'HP-01', entityStatus: 'OPEN', xPct: 55, yPct: 30, createdAt: '2026-06-10T09:00:00Z', createdByName: 'Jane PM' },
    { id: 'dp3', drawingDocId: 'doc_1', jobId: 'j1', type: 'QA_PASS', entityId: 'qa_1', entityLabel: 'QA ✓', entityStatus: 'PASS', xPct: 68, yPct: 60, createdAt: '2026-06-12T11:00:00Z', createdByName: 'Jane PM' },
    { id: 'dp4', drawingDocId: 'doc_2', jobId: 'j1', type: 'ISSUE', entityId: 'iss_1', entityLabel: 'NCR-01', entityStatus: 'OPEN', xPct: 22, yPct: 70, createdAt: '2026-06-11T14:00:00Z', createdByName: 'Jane PM' },
  ]
}

export function getDrawingPins(drawingDocId: string): DrawingPin[] {
  return _drawingPins.filter(p => p.drawingDocId === drawingDocId)
}

export function addDrawingPin(pin: Omit<DrawingPin, 'id'>): DrawingPin {
  const entry: DrawingPin = { id: `dp_${Date.now()}`, ...pin }
  _drawingPins.push(entry)
  return entry
}

export const DRAWING_PIN_META: Record<DrawingPinType, { label: string; color: string; bg: string; border: string }> = {
  WI:        { label: 'Work instruction', color: 'text-blue-700',   bg: 'bg-blue-500',   border: 'border-blue-700'   },
  ITP_HOLD:  { label: 'Hold point',       color: 'text-orange-700', bg: 'bg-orange-500', border: 'border-orange-700' },
  ISSUE:     { label: 'Issue / NCR',      color: 'text-red-700',    bg: 'bg-red-500',    border: 'border-red-700'    },
  QA_PASS:   { label: 'QA — Pass',        color: 'text-green-700',  bg: 'bg-green-500',  border: 'border-green-700'  },
  QA_FAIL:   { label: 'QA — Fail',        color: 'text-red-700',    bg: 'bg-red-600',    border: 'border-red-700'    },
  PHOTO:     { label: 'Site photo',       color: 'text-purple-700', bg: 'bg-purple-500', border: 'border-purple-700' },
}

// ── Agent Activity Log ─────────────────────────────────────────────

function seedAgentActivity(): AgentActivityEntry[] {
  return [
    {
      id: 'aa1', jobId: 'j1', agentCode: 'A09', agentName: 'QA Specialist',
      agentWave: 'Wave 1', action: 'ITP generated from MRTS specifications',
      detail: 'Drew on MRTS11, MRTS50, MRTS73. Created 4 hold points (3 HP, 1 WP) for drainage and pavement activities. Hold points added to itp_hold_points register.',
      confidence: 'HIGH', autonomyLevel: 2, timestamp: '2026-07-15T09:22:00Z',
      humanResponse: 'ACCEPTED', linkedModule: 'ITP & Hold Points', linkedRecordId: 'itp_1',
    },
    {
      id: 'aa2', jobId: 'j1', agentCode: 'A04', agentName: 'WHS Officer',
      agentWave: 'Wave 1', action: 'SWMS reviewed — HRCW triggers identified',
      detail: 'Reviewed SWMS-J001-001 (HV cable installation). Identified: energised electrical work (r.291(a)), traffic management requirement (r.291(r)), working at height >2m (r.291(d)). All 3 triggers confirmed in SWMS controls.',
      confidence: 'HIGH', autonomyLevel: 1, timestamp: '2026-07-14T14:08:00Z',
      humanResponse: 'ACCEPTED', linkedModule: 'SWMS', linkedRecordId: 'swms_1',
    },
    {
      id: 'aa3', jobId: 'j1', agentCode: 'A09', agentName: 'QA Specialist',
      agentWave: 'Wave 1', action: 'Drawing IFC reviewed — hold points updated',
      detail: 'Reviewed Civil-Layout-Rev-B.pdf (IFC). Identified revision changes: kerb alignment shifted 150mm at Ch.0+480. Existing HP-03 (kerb and channel) updated with revised chainage reference. MRTS05 clause 3.2 cited.',
      confidence: 'MEDIUM', autonomyLevel: 2, timestamp: '2026-07-13T11:34:00Z',
      humanResponse: 'MODIFIED', linkedModule: 'Drawings', linkedRecordId: 'doc_2',
    },
    {
      id: 'aa4', jobId: 'j3', agentCode: 'A04', agentName: 'WHS Officer',
      agentWave: 'Wave 1', action: 'Incident classified — WorkSafe notification not required',
      detail: 'Reviewed incident INC-J003-002 (near-miss, moving plant). Classified as: near-miss, WHS Act s.38 — notifiable if injury occurs. No statutory notification required for this near-miss under r.699. PM advised to review plant movement procedure.',
      confidence: 'HIGH', autonomyLevel: 1, timestamp: '2026-07-12T16:45:00Z',
      humanResponse: 'ACCEPTED', linkedModule: 'Incidents', linkedRecordId: 'inc_1',
    },
    {
      id: 'aa5', jobId: 'j2', agentCode: 'A09', agentName: 'QA Specialist',
      agentWave: 'Wave 1', action: 'Handover QA checklist generated',
      detail: 'Handover initiated for J002 (Maroochydore Rd). Generated checklist: 12 items. Identified 3 outstanding items: as-built drawing not confirmed for Sheet 4, NCR-J002-003 still open, 1 outstanding hold point (concrete curing WP-02).',
      confidence: 'HIGH', autonomyLevel: 2, timestamp: '2026-07-10T08:15:00Z',
      humanResponse: 'PENDING', linkedModule: 'Handover', linkedRecordId: 'ho_1',
    },
    // ── A13 CA Agent (Wave 2) ─ seeded activity showing past actions across contracts
    {
      id: 'aa6', jobId: 'j1', agentCode: 'A13', agentName: 'Contracts Administrator',
      agentWave: 'Wave 2', action: 'Variation VR-004 notice deadline identified — URGENT',
      detail: 'VR-004 (Pole 3 location shift, $3,200) notice due 20 Jun 2026 under AS4000 Clause 41.1. 3 days remaining as at 17 Jun. Draft notice letter prepared using MCB standard template. Notice not yet issued. PM review and issue required today.',
      confidence: 'HIGH', autonomyLevel: 2, timestamp: '2026-06-17T07:00:00Z',
      humanResponse: 'PENDING', linkedModule: 'Variations', linkedRecordId: 'vr4',
    },
    {
      id: 'aa7', jobId: 'j4', agentCode: 'A13', agentName: 'Contracts Administrator',
      agentWave: 'Wave 2', action: 'Variation VR-002 notice deadline — WARNING (7 days)',
      detail: 'VR-002 (HV joint design change by Energex, $12,800) notice due 24 Jun 2026 under NEC4 Clause 61.3. 7 days remaining as at 17 Jun. Notice has not been issued. Draft compensation event notice prepared. Review required this week.',
      confidence: 'HIGH', autonomyLevel: 2, timestamp: '2026-06-17T07:01:00Z',
      humanResponse: 'PENDING', linkedModule: 'Variations', linkedRecordId: 'vr2',
    },
    {
      id: 'aa8', jobId: 'j2', agentCode: 'A13', agentName: 'Contracts Administrator',
      agentWave: 'Wave 2', action: 'Performance bond expiry flagged — con2 January 2027',
      detail: 'Contract con2 (AS4000, TP-2026-002, $520,000) requires a performance bond of $52,000 (10% of contract value). Bond was due within 14 days of award (28 May 2026). Bond expiry: January 2027. Confirm bond issued and lodge expiry alert with your insurer.',
      confidence: 'HIGH', autonomyLevel: 1, timestamp: '2026-06-17T07:05:00Z',
      humanResponse: 'PENDING', linkedModule: 'Contracts', linkedRecordId: 'con2',
    },
    {
      id: 'aa9', jobId: 'j1', agentCode: 'A13', agentName: 'Contracts Administrator',
      agentWave: 'Wave 2', action: 'Progress Claim PC-001 superintendent clock — tracking',
      detail: 'PC-001 (TP-2026-001, $86,687 net) was submitted 31 May 2026. Superintendent certified 7 Jun 2026 (within AS4000 Cl.44.4 15-business-day obligation). Payment due 21 Jun 2026. Monitor receipt — no cash flow issue if paid on time.',
      confidence: 'HIGH', autonomyLevel: 1, timestamp: '2026-06-14T09:00:00Z',
      humanResponse: 'ACCEPTED', linkedModule: 'Progress Claims', linkedRecordId: 'pc1',
    },
    {
      id: 'aa10', jobId: 'j1', agentCode: 'A13', agentName: 'Contracts Administrator',
      agentWave: 'Wave 2', action: 'Running contract sum updated — VR-001 approved',
      detail: 'VR-001 (Night works $18,450) approved by TMR superintendent 7 Jun 2026. Running contract sum for con1 updated: original $385,000 + approved variations $18,450 = adjusted sum $403,450. Progress Claim PC-002 claim ceiling updated accordingly.',
      confidence: 'HIGH', autonomyLevel: 3, timestamp: '2026-06-08T10:00:00Z',
      humanResponse: 'ACCEPTED', linkedModule: 'Variations', linkedRecordId: 'vr1',
    },
  ]
}

export function getAgentActivityForJob(jobId: string): AgentActivityEntry[] {
  return _agentActivity.filter(e => e.jobId === jobId).sort((a, b) => b.timestamp.localeCompare(a.timestamp))
}

export function addAgentActivity(entry: Omit<AgentActivityEntry, 'id'>): AgentActivityEntry {
  const e: AgentActivityEntry = { id: `aa_${Date.now()}`, ...entry }
  _agentActivity.push(e)
  return e
}

export const AGENT_AUTONOMY_META: Record<1 | 2 | 3, { label: string; bg: string; text: string }> = {
  1: { label: 'Advisory',  bg: 'bg-blue-100',   text: 'text-blue-700'   },
  2: { label: 'Assisted',  bg: 'bg-amber-100',  text: 'text-amber-700'  },
  3: { label: 'Automated', bg: 'bg-purple-100', text: 'text-purple-700' },
}

export const AGENT_RESPONSE_META: Record<AgentActivityEntry['humanResponse'], { label: string; bg: string; text: string }> = {
  ACCEPTED: { label: 'Accepted',  bg: 'bg-green-100',  text: 'text-green-700'  },
  MODIFIED: { label: 'Modified',  bg: 'bg-blue-100',   text: 'text-blue-700'   },
  REJECTED: { label: 'Rejected',  bg: 'bg-red-100',    text: 'text-red-700'    },
  PENDING:  { label: 'Pending',   bg: 'bg-amber-100',  text: 'text-amber-700'  },
  IGNORED:  { label: 'Ignored',   bg: 'bg-gray-100',   text: 'text-gray-500'   },
}

// ───────────────────────────────────────────────────────────────
// PM-08: TOOLBOX TALK DRAFTS (A04 WHS Officer from SWMS)
// ───────────────────────────────────────────────────────────────

export interface ToolboxDraft {
  id: string
  swmsId: string
  swmsNumber: string
  jobId: string
  jobNumber: string
  title: string
  keyHazards: string[]
  controlsHighlight: string[]
  agentNote: string
  createdAt: string
  status: 'DRAFT_REVIEW' | 'ACTIVATED' | 'REJECTED'
}

let _toolboxDrafts: ToolboxDraft[] = []

export function createToolboxDraft(swms: {
  id: string; swmsNumber: string; jobId: string; jobNumber: string; title: string
  hazards: Array<{ description: string; controls: string[]; ratingBefore: number }>
}): ToolboxDraft {
  const topHazards = [...swms.hazards].sort((a, b) => b.ratingBefore - a.ratingBefore).slice(0, 4)
  const draft: ToolboxDraft = {
    id: `ttd_${Date.now()}`,
    swmsId: swms.id,
    swmsNumber: swms.swmsNumber,
    jobId: swms.jobId,
    jobNumber: swms.jobNumber,
    title: `${swms.swmsNumber} — Pre-Start Toolbox Talk`,
    keyHazards: topHazards.map(h => h.description),
    controlsHighlight: topHazards.flatMap(h => h.controls.slice(0, 2)).slice(0, 6),
    agentNote: `Auto-drafted by A04 WHS Officer from ${swms.swmsNumber}. Covers ${topHazards.length} highest-risk hazards. Review and activate before pre-start briefing.`,
    createdAt: new Date().toISOString(),
    status: 'DRAFT_REVIEW',
  }
  _toolboxDrafts.push(draft)
  addAgentActivity({
    jobId: swms.jobId,
    agentCode: 'A04', agentName: 'WHS Officer', agentWave: 'Wave 1',
    action: `Toolbox Talk drafted from ${swms.swmsNumber}`,
    detail: `SWMS approved — pre-start toolbox talk auto-drafted covering ${topHazards.length} highest-risk hazards. WHS Act 2011 (Qld) s.47 — duty to provide safety information. PM or WHSO to review before crew briefing.`,
    confidence: 'HIGH', autonomyLevel: 2, timestamp: new Date().toISOString(),
    humanResponse: 'PENDING', linkedModule: 'Toolbox & Alerts', linkedRecordId: draft.id,
  })
  return draft
}

export function getToolboxDrafts(jobId?: string): ToolboxDraft[] {
  return jobId ? _toolboxDrafts.filter(d => d.jobId === jobId) : [..._toolboxDrafts]
}

export function activateToolboxDraft(id: string): void {
  _toolboxDrafts = _toolboxDrafts.map(d => d.id === id ? { ...d, status: 'ACTIVATED' as const } : d)
}

// ───────────────────────────────────────────────────────────────
// PM-05: IFC → WI DRAFT (A09 QA Specialist from IFC drawing)
// ───────────────────────────────────────────────────────────────

export interface IFCWIDraft {
  id: string
  drawingDocId: string
  drawingTitle: string
  jobId: string
  title: string
  steps: Array<{
    number: number
    title: string
    instruction: string
    isHoldPoint?: boolean
    holdPointRef?: string
  }>
  holdPoints: string[]
  agentNote: string
  createdAt: string
  status: 'DRAFT_REVIEW' | 'ACTIVATED' | 'REJECTED'
}

let _ifcWIDrafts: IFCWIDraft[] = []

export function createIFCWIDraft(
  doc: { id: string; title: string; jobId: string },
  holdPoints: string[],
  taskTitles: string[]
): IFCWIDraft {
  const hpSteps = holdPoints.map((hp, i) => ({
    number: i + 1,
    title: hp.split(' — ')[0],
    instruction: `Conduct inspection per ${hp.split('(')[1]?.replace(')', '') ?? 'applicable specification'}. Obtain inspector sign-off on ITP form before proceeding. All results to be recorded.`,
    isHoldPoint: hp.includes('Hold Point'),
    holdPointRef: hp.split(' — ')[0],
  }))
  const taskSteps = taskTitles.slice(0, 3).map((t, i) => ({
    number: holdPoints.length + i + 1,
    title: t,
    instruction: `Execute ${t} in accordance with the approved work instruction, SWMS, and IFC drawing. Record as-built measurements and complete the relevant ITP hold point before proceeding.`,
  }))
  const steps = [...hpSteps, ...taskSteps]

  const draft: IFCWIDraft = {
    id: `ifc_wi_${Date.now()}`,
    drawingDocId: doc.id,
    drawingTitle: doc.title,
    jobId: doc.jobId,
    title: `${doc.title} — Field Work Package (IFC draft)`,
    steps,
    holdPoints,
    agentNote: `Generated by A09 QA Specialist from IFC drawing “${doc.title}”. ${steps.length} steps including ${hpSteps.filter(s => s.isHoldPoint).length} hold point(s) and ${taskSteps.length} work task(s). PM to review, add site-specific notes, then deploy to crew.`,
    createdAt: new Date().toISOString(),
    status: 'DRAFT_REVIEW',
  }
  _ifcWIDrafts.push(draft)
  addAgentActivity({
    jobId: doc.jobId,
    agentCode: 'A09', agentName: 'QA Specialist', agentWave: 'Wave 1',
    action: `Work Instruction drafted from IFC — ${doc.title}`,
    detail: `IFC set on ${doc.title}. Generated ${steps.length}-step work package from ${holdPoints.length} hold point(s) and ${taskTitles.slice(0,3).length} extracted task(s). MRTS references embedded. PM review required before crew deployment.`,
    confidence: 'HIGH', autonomyLevel: 2, timestamp: new Date().toISOString(),
    humanResponse: 'PENDING', linkedModule: 'Work Instructions', linkedRecordId: draft.id,
  })
  return draft
}

export function getIFCWIDrafts(jobId?: string): IFCWIDraft[] {
  return jobId ? _ifcWIDrafts.filter(d => d.jobId === jobId) : [..._ifcWIDrafts]
}

export function activateIFCWIDraft(id: string): void {
  _ifcWIDrafts = _ifcWIDrafts.map(d => d.id === id ? { ...d, status: 'ACTIVATED' as const } : d)
}

// ───────────────────────────────────────────────────────────────
// PM-09: STEP PTW REQUIREMENT DETECTION (keyword-based)
// ───────────────────────────────────────────────────────────────

export interface StepPTWCheck {
  ptwType: string
  label: string
  icon: string
}

/**
 * Keyword-based detection: returns the PTW type needed for a WI step,
 * or null if no PTW is required. Matches common civil/electrical keywords.
 */
export function checkStepPTWRequirement(title: string, instruction: string): StepPTWCheck | null {
  const text = (title + ' ' + (instruction ?? '')).toLowerCase()
  if (/\b(excavat|dig\b|pit.work|trench|conduit.lay|byda|dial before)/i.test(text))
    return { ptwType: 'EXCAVATION', label: 'Excavation PTW', icon: '🕳' }
  if (/\b(hv\b|high.voltage|energis|live.cable|switching|11kv|415v|330v|lv panel|termination.*energis)/i.test(text))
    return { ptwType: 'ENERGISED_ELECTRICAL', label: 'HV Electrical PTW', icon: '⚡' }
  if (/\b(confined.space|pit.entry|vault|manhole|gas.test|atmospheric|cse\b)/i.test(text))
    return { ptwType: 'CONFINED_SPACE', label: 'Confined Space PTW', icon: '\uD83D\uDD12' }
  if (/\b(overhead|ewp\b|elevated.work.platform|boom.lift|cherry.picker|working.at.height|ladder)/i.test(text))
    return { ptwType: 'WORKING_AT_HEIGHTS', label: 'Heights PTW', icon: '⬆' }
  return null
}

// ───────────────────────────────────────────────────────────────
// PM-11: XERO SYNC STATE (A08 Financial Agent, Wave 4)
// ───────────────────────────────────────────────────────────────

export interface XeroSyncRecord {
  claimId: string
  xeroInvoiceId: string
  syncedAt: string
  syncedByName: string
  jobId: string
}

let _xeroSyncs: XeroSyncRecord[] = []

export function getXeroSync(claimId: string): XeroSyncRecord | null {
  return _xeroSyncs.find(s => s.claimId === claimId) ?? null
}

export function getAllXeroSyncs(): XeroSyncRecord[] {
  return [..._xeroSyncs]
}

export function recordXeroSync(claimId: string, jobId: string, userName: string): XeroSyncRecord {
  const xeroId = `XR-${new Date().getFullYear()}-${String(Math.floor(1000 + Math.random() * 9000))}`
  const record: XeroSyncRecord = {
    claimId,
    xeroInvoiceId: xeroId,
    syncedAt: new Date().toISOString(),
    syncedByName: userName,
    jobId,
  }
  _xeroSyncs = _xeroSyncs.filter(s => s.claimId !== claimId)
  _xeroSyncs.push(record)
  addAgentActivity({
    jobId,
    agentCode: 'A08', agentName: 'Financial Agent', agentWave: 'Wave 4',
    action: `Claim ${claimId} → Xero invoice ${xeroId}`,
    detail: `Progress payment claim ${claimId} pushed to Xero Accounts Receivable as invoice ${xeroId}. Awaiting client payment within 15 business days (BIF Act 2017 (Qld) s.68). A/R aged from today. OAuth token used — Xero connection valid for 30 days.`,
    confidence: 'HIGH', autonomyLevel: 1, timestamp: new Date().toISOString(),
    humanResponse: 'PENDING', linkedModule: 'Progress Payment Claims', linkedRecordId: claimId,
  })
  return record
}

// ───────────────────────────────────────────────────────────────
// WHS SPRINT ITEM 11 — A04 REG 291 CLASSIFIER
// Keyword-based classification of activity descriptions against
// Schedule 2, WHS Regulation 2011 (Qld) — High Risk Construction Work.
// Used by WHSCoverageMapping to auto-classify newly added activities
// and by the A04 WHS Officer agent during SWMS review.
// Mirrors the pattern of checkStepPTWRequirement (PM-09).
// ───────────────────────────────────────────────────────────────

const REG_291_RULES: Array<{ category: string; pattern: RegExp }> = [
  { category: 'Working at heights with risk of fall > 2 m',
    pattern: /\b(ewp|elevated.work.platform|boom.lift|cherry.picker|ladder|scaffold|height|roof|pole.top|at.height|fall.risk|working.above|gantry|overhead.work)/i },
  { category: 'Trench or shaft excavation deeper than 1.5 m',
    pattern: /\b(excav|dig\b|trench|pit.work|conduit.lay|byda|service.loc|boring|shaft|cut|slot|saw.cut)/i },
  { category: 'Confined space',
    pattern: /\b(confined.space|cse\b|pit.entry|vault|manhole|cable.vault|entry.into|gas.test|atmospheric.test)/i },
  { category: 'Energised electrical installation or services',
    pattern: /\b(energis|live.cable|lv\b|415v|240v|electrical.conn|switchboard|msb|isolat|live.work|loto|termination|cable.pulling|pit.electric|signal.head|cabinet.install|controller.install)/i },
  { category: 'Energised electrical — high voltage',
    pattern: /\b(hv\b|high.voltage|11kv|22kv|66kv|132kv|hv.cable|hv.jointing|hv.switching|switching.auth)/i },
  { category: 'Adjacent to road in use by traffic',
    pattern: /\b(road\b|traffic|highway|motorway|street|lane|intersection|carriageway|tmc\b|tcp\b|tmp\b|shoulder|median|kerb|footpath.*road|adjacent.*road|night.work|traffic.signal|signal.head|ped.crossing|pedestrian.*road)/i },
  { category: 'Demolition',
    pattern: /\b(demolish|demolition|remove.existing|remove.*structure|strip|dismantle)/i },
  { category: 'Tilt-up or precast concrete elements',
    pattern: /\b(tilt.up|precast|concrete.panel|concrete.slab.lift|structural.concrete)/i },
  { category: 'Movement of powered mobile plant',
    pattern: /\b(excavator|bobcat|tipper|crane|forklift|telehandler|roller|grader|loader|plant.movement|mobile.plant|truck.mov)/i },
  { category: 'Pressurised gas lines or equipment',
    pattern: /\b(gas.line|gas.main|pressurised.gas|lpg|natural.gas|gas.pipe)/i },
  { category: 'Risk of drowning',
    pattern: /\b(drown|water.body|river|creek|drain|stormwater|flood|over.water|marine)/i },
  { category: 'Explosive-powered tools',
    pattern: /\b(explosive|ram.set|cartridge.tool|powder.actuated|propellant)/i },
]

/**
 * Classify an activity description against Reg 291 Schedule 2 HRCW categories.
 * Returns matched category strings (may be empty if no HRCW detected).
 * Used by A04 WHS Officer agent and WHSCoverageMapping manual-add flow.
 */
export function classifyReg291(description: string): string[] {
  const categories: string[] = []
  for (const rule of REG_291_RULES) {
    if (rule.pattern.test(description)) {
      categories.push(rule.category)
    }
  }
  return categories
}

/**
 * Determine if the classified categories constitute HRCW.
 * If any categories are returned, a SWMS is required under Reg 291.
 */
export function requiresSWMS(categories: string[]): boolean {
  return categories.length > 0
}

/**
 * Run A04 agent analysis across all activities in a job,
 * return activities whose current Reg 291 categories appear incomplete
 * (i.e. classifier detects categories not yet in the activity record).
 */
export interface Reg291ClassificationResult {
  activityId:        string
  description:       string
  detectedCategories: string[]
  existingCategories: string[]
  newCategories:     string[]  // detected but not in existing
  confidence:        'HIGH' | 'MEDIUM'
}

export function runA04GapDetection(
  activities: Array<{ id: string; activityDescription: string; reg291Categories: string[] }>
): Reg291ClassificationResult[] {
  const results: Reg291ClassificationResult[] = []
  for (const a of activities) {
    const detected  = classifyReg291(a.activityDescription)
    const existing  = a.reg291Categories ?? []
    const newCats   = detected.filter(d => !existing.includes(d))
    if (newCats.length > 0) {
      results.push({
        activityId:         a.id,
        description:        a.activityDescription,
        detectedCategories: detected,
        existingCategories: existing,
        newCategories:      newCats,
        confidence:         newCats.length <= 2 ? 'HIGH' : 'MEDIUM',
      })
    }
  }
  return results
}
// ───────────────────────────────────────────────────────────────

export interface JobSubcontractor {
  id: string
  jobId: string
  companyName: string
  abn: string
  contactName: string
  contactPhone: string
  contactEmail: string
  role: string
  status: 'ACTIVE' | 'COMPLETED' | 'ON_HOLD'
  contractValue?: number
  startDate?: string
  endDate?: string
  insuranceExpiry?: string
  licenceNumber?: string
}

const _JOB_SUBCONTRACTORS: JobSubcontractor[] = [
  {
    id: 'sc1', jobId: 'j1',
    companyName: 'AllCity Traffic Control Pty Ltd',
    abn: '77 123 456 789',
    contactName: 'Darren Walsh',
    contactPhone: '0412 789 012',
    contactEmail: 'dwalsh@allcitytraffic.com.au',
    role: 'Traffic Management — TMC supply and TCP services',
    status: 'ACTIVE',
    contractValue: 28500,
    startDate: '2026-05-20',
    endDate: '2026-07-31',
    insuranceExpiry: '2026-12-31',
    licenceNumber: 'QLD-TMC-2022-04451',
  },
  {
    id: 'sc2', jobId: 'j1',
    companyName: 'Queensland Concrete Services Pty Ltd',
    abn: '44 987 654 321',
    contactName: 'Mick Saunders',
    contactPhone: '0418 345 678',
    contactEmail: 'msaunders@qlconcrete.com.au',
    role: 'Concrete pit construction (signal pits and pit lids)',
    status: 'COMPLETED',
    contractValue: 14200,
    startDate: '2026-05-28',
    endDate: '2026-06-10',
    insuranceExpiry: '2027-01-15',
  },
  {
    id: 'sc3', jobId: 'j4',
    companyName: 'Energex Switching Services',
    abn: '93 054 929 817',
    contactName: 'Terry Nguyen',
    contactPhone: '13 62 62',
    contactEmail: 'switching@energex.com.au',
    role: 'HV switching authority holder (11kV authorisation)',
    status: 'ACTIVE',
    startDate: '2026-06-19',
    licenceNumber: 'QLD-HV-AUTH-2024-1182',
  },
  {
    id: 'sc4', jobId: 'j4',
    companyName: 'IEC Cable & Connector Solutions Pty Ltd',
    abn: '61 456 789 123',
    contactName: 'Priya Mehta',
    contactPhone: '0421 987 654',
    contactEmail: 'pmehta@iec-cable.com.au',
    role: 'HV cable supply, delivery and splicing materials',
    status: 'ACTIVE',
    contractValue: 98400,
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    insuranceExpiry: '2026-11-30',
  },
  {
    id: 'sc5', jobId: 'j1',
    companyName: 'Linehunt Locating Services',
    abn: '28 712 398 051',
    contactName: 'Rick Holloway',
    contactPhone: '0422 111 222',
    contactEmail: 'rholloway@linehunt.com.au',
    role: 'Underground service locating (EM + GPR) pre-excavation',
    status: 'COMPLETED',
    contractValue: 2800,
    startDate: '2026-05-18',
    endDate: '2026-05-19',
    insuranceExpiry: '2027-03-01',
    licenceNumber: 'QLD-UTIL-LOC-0382',
  },
]

export function getSubcontractorsForJob(jobId: string): JobSubcontractor[] {
  return _JOB_SUBCONTRACTORS.filter(sc => sc.jobId === jobId)
}
